import { useEffect, useRef, useState, type ElementType, type ReactNode } from 'react';

export type RevealMotion =
  'rise' | 'riseTall' | 'slideLeft' | 'slideRight' | 'zoom' | 'fade' | 'blurIn' | 'clipUp';

type RevealProps<T extends ElementType = 'section'> = {
  as?: T;
  className?: string;
  children: ReactNode;
  /**
   * Opacity-only reveal (no translate). Use when children need position: sticky — a transformed
   * ancestor breaks sticky relative to the document scrollport.
   */
  fadeOnly?: boolean;
  /** Initial offset direction before the section is revealed (IntersectionObserver + CSS transition). */
  motion?: RevealMotion;
  /** Cascade entrance on `.ml-revealStaggerItem` descendants instead of animating the wrapper. */
  stagger?: boolean;
  /** Reveal once and stop observing — no replay. Use for expensive variants (blur / clip). */
  once?: boolean;
};

/**
 * Fade/slide in when the block enters the viewport. When it leaves (no intersection with the
 * viewport), the pre-reveal state is restored so the entrance can run again on the next approach.
 *
 * Note: with `threshold: 0`, `isIntersecting === false` means zero overlap with the viewport.
 * We arm off on that alone — requiring an extra “past by N px” margin prevented `armOff` when the
 * section had just left (e.g. `bottom` slightly negative), so replay never happened.
 */
export function Reveal<T extends ElementType = 'section'>({
  as,
  className = '',
  children,
  fadeOnly = false,
  motion = 'rise',
  stagger = false,
  once = false,
}: RevealProps<T>) {
  const Tag = (as ?? 'section') as ElementType;
  const ref = useRef<HTMLElement | null>(null);
  const [on, setOn] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let armed = false;

    const armOn = () => {
      armed = true;
      setOn(true);
    };

    const armOff = () => {
      if (once || !armed) return;
      armed = false;
      setOn(false);
    };

    const io = new IntersectionObserver(
      ([e]) => {
        if (!e) return;
        if (e.isIntersecting) {
          armOn();
          if (once) io.disconnect();
        } else {
          armOff();
        }
      },
      { root: null, rootMargin: '0px', threshold: 0 }
    );
    io.observe(el);

    const checkVisible = () => {
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const vw = window.innerWidth;
      if (r.width <= 0 || r.height <= 0) return;
      const visible = r.bottom > 0 && r.top < vh && r.right > 0 && r.left < vw;
      if (visible) {
        armOn();
      } else {
        armOff();
      }
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(checkVisible);
    });

    return () => io.disconnect();
  }, [as, fadeOnly, motion, stagger, once]);

  const motionClass = fadeOnly ? 'ml-reveal--m-fade' : `ml-reveal--m-${motion}`;
  const staggerClass = stagger ? ' ml-reveal--stagger' : '';

  return (
    <Tag
      ref={ref as any}
      className={`${className} ml-reveal${fadeOnly ? ' ml-reveal--fadeOnly' : ''}${staggerClass} ${motionClass} ${on ? 'ml-reveal--on' : ''}`.trim()}>
      {children}
    </Tag>
  );
}
